import { useEffect } from 'react'
import CableApp from 'actioncable'
import { accessTokens } from 'api/accessTokenMgmt'
import { withChannelAuth } from 'state/channels/channelAuth'
import type { BsvDeviceTransferSession } from 'api/api'
import useStore from 'state/knovStore'
import { BsvWalletType } from 'wallets/wallets'
import queryClient from 'api/queryClient'
import { WALLET_DETAILS_QUERY_KEY, type WalletDetails } from 'api/useWalletDetails'
import { handleMediaDockPlaylistChanged } from 'state/mediaPlaybackCoordinator'
import { dispatchUserFeedTick } from 'state/feedTicks'
import { dispatchOrdinalStreamUpdate } from 'state/ordinalStreamUpdates'
import {
    getClientSigningStatus,
    processSigningRequest,
    startSigningRequestPolling,
    stopSigningRequestPolling,
    type SigningRequest,
} from 'lib/client-signing-flow'
import { CLIENT_SIGNING_READY_EVENT, currentUserUsesClientSigning } from 'lib/clientSigningFeature'
import { SHADOW_PROBE_EVENT } from 'lib/localSigningShadow'

export const WALLET_BALANCE_EVENT = 'treechat:wallet-balance-updated'
export const DEVICE_TRANSFER_SESSION_EVENT = 'treechat:device-transfer-session'

export interface DeviceTransferSessionEventDetail {
    eventType: string
    deviceTransferSession: BsvDeviceTransferSession
}

const pendingDeviceTransferSessions = new Map<string, BsvDeviceTransferSession>()

export function getPendingDeviceTransferSessions(): BsvDeviceTransferSession[] {
    return Array.from(pendingDeviceTransferSessions.values())
}

/** Real-time payment status update, keyed on the client-supplied payment_id (== BsvTx id). */
export interface PaymentUpdate {
    bsvTxId: string
    status: string
    txId?: string
    amountSats?: number
    toAddress?: string
    lastError?: string
}

const TERMINAL_PAYMENT_STATUSES = [
    'broadcasted',
    'finished',
    'confirmed',
    'completed',
    'failed',
    'cancelled',
] as const
const SUCCESS_PAYMENT_STATUSES = ['broadcasted', 'finished', 'confirmed', 'completed'] as const

export function isTerminalPaymentStatus(status: string): boolean {
    return (TERMINAL_PAYMENT_STATUSES as readonly string[]).includes(status)
}

export function isSuccessfulPaymentStatus(status: string): boolean {
    return (SUCCESS_PAYMENT_STATUSES as readonly string[]).includes(status)
}

// Per-payment listeners so a send surface (Wallet modal, !pay composer) can await the
// terminal outcome of the exact payment it initiated, rather than the removed unscoped
// 'sent/failed bsv transaction' broadcasts that fired for any of the user's transactions.
const paymentUpdateListeners = new Map<string, Set<(update: PaymentUpdate) => void>>()

export function subscribePaymentUpdate(
    paymentId: string,
    callback: (update: PaymentUpdate) => void,
): () => void {
    let listeners = paymentUpdateListeners.get(paymentId)
    if (!listeners) {
        listeners = new Set()
        paymentUpdateListeners.set(paymentId, listeners)
    }
    listeners.add(callback)

    return () => {
        const set = paymentUpdateListeners.get(paymentId)
        if (!set) return
        set.delete(callback)
        if (set.size === 0) paymentUpdateListeners.delete(paymentId)
    }
}

function dispatchPaymentUpdate(update: PaymentUpdate) {
    const listeners = paymentUpdateListeners.get(update.bsvTxId)
    if (!listeners) return
    listeners.forEach(cb => {
        try {
            cb(update)
        } catch (error) {
            console.error('>>> payment update listener error:', error)
        }
    })
}

export function clearPendingDeviceTransferSession(sessionId: string): void {
    pendingDeviceTransferSessions.delete(sessionId)
}

/** Shape of an upvalue history entry from the BSV service. */
interface BsvUpvalueHistoryEntry {
    id: number
    amount: string
    transaction_type: string
    created_at: string
    from_user_id: number
    to_user_id: number
    is_boost?: boolean
    [key: string]: unknown
}

const msgTypes = {
    USER_BSV_BALANCE_UPDATED: 'update user bsv balance',
    USER_BSV_ADDRESS_UPDATED: 'update user bsv address',
    USER_BSV_TRANSACTION_SENT: 'sent bsv transaction',
    USER_BSV_TRANSACTION_FAIL: 'failed bsv transaction',
    USER_BSV_UPVALUE: 'upvalue',
    USER_BSV_PAYMENT_UPDATE: 'bsv_payment_update',
    USER_DEVICE_TRANSFER_SESSION: 'device_transfer_session',
    USER_MEDIA_DOCK_PLAYLIST_CHANGED: 'media_dock_playlist_changed',
    USER_LOCAL_SIGNING_SHADOW_PROBE: 'local_signing_shadow_probe',
    USER_SIDEBAR_ACTIVITY_UPDATED: 'sidebar activity updated',
    USER_HISTORY_UPDATED: 'history updated',
    USER_FEED_TICK: 'feed tick',
}

type InvalidationTimer = ReturnType<typeof setTimeout>

const sidebarInvalidationTimers = new Map<string, InvalidationTimer>()
const ACTIONS_QUERY_KEY = ['actions'] as const
const PEOPLE_CHATS_QUERY_KEY = ['peopleChats'] as const

function scheduleQueryInvalidation(
    timerKey: string,
    queryKey: readonly unknown[],
    delayMs: number,
) {
    const existingTimer = sidebarInvalidationTimers.get(timerKey)
    if (existingTimer) {
        clearTimeout(existingTimer)
    }

    const timer = setTimeout(() => {
        sidebarInvalidationTimers.delete(timerKey)
        queryClient.invalidateQueries({ queryKey: [...queryKey] })
    }, delayMs)
    sidebarInvalidationTimers.set(timerKey, timer)
}

function scheduleActionsInvalidation(delayMs: number, keySuffix: string) {
    scheduleQueryInvalidation(`actions:${keySuffix}`, ACTIONS_QUERY_KEY, delayMs)
}

function schedulePeopleChatsInvalidation(delayMs: number, keySuffix: string) {
    scheduleQueryInvalidation(`peopleChats:${keySuffix}`, PEOPLE_CHATS_QUERY_KEY, delayMs)
}

function scheduleSidebarActivityInvalidation() {
    scheduleActionsInvalidation(3000, 'sidebar-fast')
    scheduleActionsInvalidation(12000, 'sidebar-es-trailing')
    schedulePeopleChatsInvalidation(15000, 'sidebar-es-trailing')
}

function scheduleHistoryUpdatedInvalidation() {
    scheduleActionsInvalidation(3000, 'history')
}

function scheduleReconnectInvalidation() {
    scheduleActionsInvalidation(1000, 'reconnect')
    if (queryClient.getQueryCache().findAll({ queryKey: [...PEOPLE_CHATS_QUERY_KEY] }).length > 0) {
        schedulePeopleChatsInvalidation(15000, 'reconnect')
    }
}

const buildSigningRequestSubscriptionParams = (deviceId: string) => {
    const tokens = accessTokens()
    if (!tokens?.uid || !tokens.client || !tokens['access-token']) {
        return null
    }

    return {
        channel: 'SigningRequestsChannel' as const,
        deviceId,
        uid: tokens.uid,
        client: tokens.client,
        'access-token': tokens['access-token'],
    }
}

const isTerminalDeviceTransferStatus = (status: string): boolean =>
    ['completed', 'cancelled', 'expired'].includes(status)

export default function useUserChannel() {
    const activeSpaceId = useStore(state => state.activeSpaceId)

    useEffect(() => {
        if (gon.currentUser) {
            const cable = CableApp.cable
            let deviceChannel: ReturnType<typeof cable.subscriptions.create> | null = null
            let creatingDeviceChannel = false
            let isUnmounted = false
            let connectedOnce = false

            const handleSigningRequest = (signingRequest: SigningRequest) => {
                console.log(
                    '>>> Received signing request via ActionCable:',
                    signingRequest.signing_request_id,
                    signingRequest.device_id,
                )
                processSigningRequest(signingRequest).then(result => {
                    if (!result.success) {
                        console.error('>>> Failed to process signing request:', result.error)
                    }
                })
            }

            const ensureSigningRequestPolling = () => {
                if (!currentUserUsesClientSigning()) return
                startSigningRequestPolling().catch(error => {
                    console.error('>>> Failed to start signing request polling:', error)
                })
            }

            const handleDeviceTransferSession = async (
                detail: DeviceTransferSessionEventDetail,
            ) => {
                const deviceTransferSession = detail.deviceTransferSession
                if (isTerminalDeviceTransferStatus(deviceTransferSession.status)) {
                    pendingDeviceTransferSessions.delete(deviceTransferSession.id)
                } else {
                    pendingDeviceTransferSessions.set(
                        deviceTransferSession.id,
                        deviceTransferSession,
                    )
                }

                if (typeof window !== 'undefined') {
                    window.dispatchEvent(
                        new CustomEvent(DEVICE_TRANSFER_SESSION_EVENT, {
                            detail,
                        }),
                    )
                }

                if (detail.eventType !== 'requested') {
                    return
                }

                if (!currentUserUsesClientSigning()) return

                const clientSigningStatus = await getClientSigningStatus()
                if (
                    !clientSigningStatus.available ||
                    !clientSigningStatus.deviceId ||
                    clientSigningStatus.deviceId === deviceTransferSession.requesting_device_id
                ) {
                    return
                }

                const state = useStore.getState()
                const deviceTransferModalOpen =
                    state.isModalOpen &&
                    state.modalParams?.type === 'DeviceTransfer' &&
                    state.modalParams?.sessionId === deviceTransferSession.id
                if (!deviceTransferModalOpen) {
                    state.actions?.modalActions?.openDeviceTransferModal?.({
                        role: 'approver',
                        sessionId: deviceTransferSession.id,
                        initialSession: deviceTransferSession,
                        launchSource: 'realtime',
                    })
                }
            }

            const ensureDeviceSigningSubscription = async () => {
                if (!currentUserUsesClientSigning()) return
                if (deviceChannel || creatingDeviceChannel) {
                    return
                }

                creatingDeviceChannel = true
                try {
                    const clientSigningStatus = await getClientSigningStatus()
                    if (
                        isUnmounted ||
                        !clientSigningStatus.available ||
                        !clientSigningStatus.deviceId
                    ) {
                        return
                    }

                    const signingRequestParams = buildSigningRequestSubscriptionParams(
                        clientSigningStatus.deviceId,
                    )
                    if (!signingRequestParams) {
                        return
                    }

                    deviceChannel = cable.subscriptions.create(signingRequestParams, {
                        received: data => {
                            if (data.message === 'signing_request') {
                                handleSigningRequest(data.signing_request as SigningRequest)
                            }
                        },
                    })
                } finally {
                    creatingDeviceChannel = false
                }
            }

            const channel = cable.subscriptions.create(
                withChannelAuth({ channel: 'UserChannel', userId: gon.currentUser.id }),
                {
                    connected: () => {
                        if (connectedOnce) {
                            scheduleReconnectInvalidation()
                            dispatchUserFeedTick({})
                        }
                        connectedOnce = true
                        ensureSigningRequestPolling()
                        ensureDeviceSigningSubscription().catch(error => {
                            console.error(
                                '>>> Failed to subscribe to device signing channel:',
                                error,
                            )
                        })
                    },
                    received: data => {
                        if (data.message === 'new space' && data.space_id !== activeSpaceId) {
                            location.reload()
                        }

                        if (data.message === msgTypes.USER_SIDEBAR_ACTIVITY_UPDATED) {
                            scheduleSidebarActivityInvalidation()
                        }

                        if (data.message === msgTypes.USER_HISTORY_UPDATED) {
                            scheduleHistoryUpdatedInvalidation()
                        }

                        if (data.message === msgTypes.USER_FEED_TICK) {
                            dispatchUserFeedTick(data)
                        }

                        if (data.message === msgTypes.USER_BSV_BALANCE_UPDATED) {
                            // Server-side BSV events are SHUAllet events; keep them keyed consistently.
                            // console.log(`>>> Changing balance to: ${data.balance}`)
                            queryClient.setQueryData<WalletDetails>(
                                [WALLET_DETAILS_QUERY_KEY, BsvWalletType.SHUALLET],
                                (oldData: WalletDetails | undefined) => ({
                                    ...oldData,
                                    balance: data.balance,
                                    confirmedBalance: data.confirmed,
                                    unconfirmedBalance: data.unconfirmed,
                                    balanceLoaded: true,
                                }),
                            )
                            if (typeof window !== 'undefined') {
                                window.dispatchEvent(
                                    new CustomEvent(WALLET_BALANCE_EVENT, {
                                        detail: {
                                            walletType: BsvWalletType.SHUALLET,
                                            balance: data.balance,
                                            balanceLoaded: true,
                                        },
                                    }),
                                )
                            }
                        }

                        if (data.message === msgTypes.USER_BSV_ADDRESS_UPDATED) {
                            if (data.address !== '') {
                                try {
                                    const addressInfo = JSON.parse(data.address)
                                    //console.log('>>> received address info: ', addressInfo)
                                    queryClient.setQueryData<WalletDetails>(
                                        [WALLET_DETAILS_QUERY_KEY, BsvWalletType.SHUALLET],
                                        (oldData: WalletDetails | undefined) => {
                                            const address =
                                                addressInfo.address || oldData?.address || ''
                                            const ordinalsAddress =
                                                addressInfo.ordinalsAddress ||
                                                oldData?.ordinalsAddress ||
                                                ''

                                            return {
                                                ...oldData,
                                                address,
                                                ordinalsAddress,
                                                addressLoaded: Boolean(address),
                                            }
                                        },
                                    )
                                } catch (error) {
                                    console.error('Failed to parse address info:', error)
                                }
                            } else {
                                console.warn(">>> Attempted to change address to ''!")
                            }
                        }

                        if (data.message === msgTypes.USER_BSV_UPVALUE) {
                            queryClient.setQueryData<BsvUpvalueHistoryEntry[]>(
                                ['bsv-upvalue-history', gon.currentUser?.id],
                                (oldData: BsvUpvalueHistoryEntry[] | undefined) => {
                                    const existing = oldData ?? []
                                    // Ensure we don't add duplicate entries
                                    if (existing.some(item => item.id === data.upvalue.id)) {
                                        return existing
                                    }
                                    return [data.upvalue, ...existing]
                                },
                            )
                            const ownUpvalue =
                                String(data.upvalue.from_user_id) === String(gon.currentUser?.id)
                            if (data.upvalue.bsv_ordinal_id && !ownUpvalue) {
                                dispatchOrdinalStreamUpdate({
                                    ordinalId: String(data.upvalue.bsv_ordinal_id),
                                    upvalueId: data.upvalue.id,
                                })
                            }
                        }

                        if (data.message === msgTypes.USER_BSV_PAYMENT_UPDATE) {
                            // Payment status updated - invalidate payments query to trigger refetch
                            queryClient.invalidateQueries({
                                queryKey: ['bsv-payments', gon.currentUser?.id],
                            })
                            // Notify any surface awaiting this specific payment's outcome.
                            dispatchPaymentUpdate({
                                bsvTxId: data.bsv_tx_id,
                                status: data.status,
                                txId: data.tx_id,
                                amountSats: data.amount_sats,
                                toAddress: data.to_address,
                                lastError: data.last_error,
                            })
                        }

                        if (data.message === msgTypes.USER_DEVICE_TRANSFER_SESSION) {
                            handleDeviceTransferSession({
                                eventType: data.event_type,
                                deviceTransferSession:
                                    data.device_transfer_session as BsvDeviceTransferSession,
                            }).catch(error => {
                                console.error(
                                    '>>> Failed to handle device transfer session update:',
                                    error,
                                )
                            })
                        }

                        if (data.message === msgTypes.USER_LOCAL_SIGNING_SHADOW_PROBE) {
                            window.dispatchEvent(
                                new CustomEvent(SHADOW_PROBE_EVENT, { detail: data.probe }),
                            )
                        }

                        if (data.message === msgTypes.USER_MEDIA_DOCK_PLAYLIST_CHANGED) {
                            handleMediaDockPlaylistChanged({
                                revision: data.revision,
                                updatedAt: data.updatedAt,
                            })
                        }
                    },
                },
            )

            // A wallet made or restored on this device after the channel connected (a new
            // browser-wallet signup, a passkey restore) starts signing right away, without a reload.
            const onClientSigningReady = () => {
                ensureSigningRequestPolling()
                ensureDeviceSigningSubscription().catch(error => {
                    console.error('>>> Failed to subscribe to device signing channel:', error)
                })
            }
            onClientSigningReady()
            window.addEventListener(CLIENT_SIGNING_READY_EVENT, onClientSigningReady)

            return () => {
                isUnmounted = true
                window.removeEventListener(CLIENT_SIGNING_READY_EVENT, onClientSigningReady)
                stopSigningRequestPolling()
                cable.subscriptions.remove(channel)
                if (deviceChannel) {
                    cable.subscriptions.remove(deviceChannel)
                }
            }
        }
    }, [activeSpaceId])
}

// NOTE: The former useTxChannel / createTxSubscription helpers consumed the dead
// 'sent/failed bsv transaction' broadcasts (no server-side emitters) and were unscoped to a
// specific send. Payment surfaces now use subscribePaymentUpdate(paymentId, cb) instead,
// which delivers the terminal outcome of the exact payment they initiated.
